import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Device, Model } from 'icreate/common/models/device';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import { modelValidator } from 'icreate/common/validators/device/device-validator';

@Component({
    selector: 'app-model-dialog',
    templateUrl: './model-dialog.component.html',
    styleUrl: './model-dialog.component.css',
    standalone: false,
})
export class ModelDialogComponent implements OnInit {
    isEditing: boolean = false;
    repatedName: boolean = false;
    repatedItemCode: boolean = false;
    selectedDevice: Device;
    modelName: string;
    itemCode: string;
    description: string;
    validationErrors: ValidationErrorMap = {};
    hasError: boolean = false;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            selectedDevice: Device;
            model: Model;
            currentModels: Model[];
        },
        private dialogRef: MatDialogRef<ModelDialogComponent>,
        private translate: TranslateService
    ) {}

    ngOnInit(): void {
        this.selectedDevice = this.data?.selectedDevice;
        if (!this.selectedDevice) this.closeDialog();
        if (this.data.model) {
            this.modelName = this.data.model.name;
            this.itemCode = this.data.model.code;
            this.description = this.data.model.description;
            this.isEditing = true;
        }
    }

    closeDialog() {
        this.dialogRef.close();
    }

    onModelNameChange(event: { name: string; value: string }) {
        this.modelName = event.value;
        this.updateValidationErrors();
    }

    onItemCodeChange(event: { name: string; value: string }) {
        this.itemCode = event.value;
        this.updateValidationErrors();
    }

    onDescriptionChange(value) {
        this.description = value;
        this.updateValidationErrors();
    }

    save() {
        const modelData: Model = {
            id: this.data?.model?.id ?? null,
            name: this.modelName,
            code: this.itemCode,
            date: this.data?.model?.date ?? null,
            description: this.description,
            equipmentId: this.data.selectedDevice.id,
        };

        this.dialogRef.close(modelData);
    }

    isSaveDisabled() {
        if (!this.modelName?.trim() || !this.itemCode?.trim() || this.hasError) return true;

        if (this.data.model) {
            const originalModel = this.data.model;
            const nameChanged = this.modelName !== originalModel.name;
            const codeChanged = this.itemCode !== originalModel.code;
            const descriptionChanged = this.description !== originalModel.description;
            return !nameChanged && !codeChanged && !descriptionChanged;
        }

        return false;
    }

    updateValidationErrors(): void {
        const validationData: Model = {
            name: this.modelName?.trim(),
            code: this.itemCode?.trim(),
            description: this.description,
            equipmentId: this.data.selectedDevice.id,
        };
        this.repatedName = false;
        this.repatedItemCode = false;

        if (this.modelName?.trim()) {
            const isDuplicate = this.data.currentModels.some(
                model => model.name === this.modelName?.trim() && model.id !== this.data.model?.id
            );
            this.repatedName = isDuplicate;
        }

        if (this.itemCode?.trim()) {
            const isDuplicate = this.data.currentModels.some(
                model => model.code === this.itemCode?.trim() && model.id !== this.data.model?.id
            );
            this.repatedItemCode = isDuplicate;
        }

        const errors = getValidationErrors(modelValidator(), validationData);
        const mappedErrors: { [key: string]: { message: string } } = mapErrors(errors) || {};

        if (this.repatedName) {
            mappedErrors['name'] = {
                message: this.translate.instant('model.dialog.model-name.repeated.error'),
            };
        }

        if (this.repatedItemCode) {
            mappedErrors['itemCode'] = {
                message: this.translate.instant('model.dialog.item-code.repeated.error'),
            };
        }

        this.validationErrors = Object.keys(mappedErrors).length > 0 ? mappedErrors : null;

        this.hasError = Object.keys(mappedErrors).length > 0;
    }

    public get validatorFunction() {
        return modelValidator;
    }
}
